import React, { useState, useEffect, Fragment } from "react";
import "react-tooltip/dist/react-tooltip.css";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import HistoryTable from "../../clinical-services/service-steps/clinical-assessment/medical-history/historyTable";
import MedicalHistoryCreate from "../../clinical-services/service-steps/clinical-assessment/MedicalHistoryCreate";
import {
  getHistoryTypeName,
  MedicalHistoryTypes,
} from "../../clinical-services/service-steps/clinical-assessment/data";
import {
  deleteMedicalSurgery,
  fetchMedicalHistory,
  updateMedicalHistory,
} from "../../../../redux/modules/clinical-services/actions";
import { Menu, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Button from "../../../common/Button";
import ConfirmationModalNew from "../../../common/ConfirmationModalNew";
import { capitalizeFirstLetter, formatDateToDD_MMM_YYYY, formatWordsIntoLines, formatDiagnosisDateForDisplay } from "../../../../helpers/functions";
import {
  DeleteOptions,
  StatusOptions,
} from "../../clinical-services/service-steps/clinical-assessment/medical-history/options";
import NewToolTip from "../../../common/New-ToolTip";
import UncodedMedical from "../../../common/tooltip/patient/uncoded-medical";
import { ReactSVG } from "react-svg";
import { useClickOutsideTooltip } from "../../../../hooks/useClickOutsideTooltip";

const PatientSurgeries = ({}) => {
  const dispatch = useDispatch();
  //#region Data
  const { medicalHistory } = useSelector((state) => state.clinicalService);
  const { id: patientId } = useParams();

  const [medicalSurgeriesRecords, setMedicalSurgeriesRecords] = useState([]);
  //#endregion

  //#region Pop-ups
  const [showCreateSlider, setShowCreateSlider] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [selectedHistoryType, setSelectedHistoryType] = useState(
    MedicalHistoryTypes.Surgery
  );
  const [create, setCreate] = useState(true);
  const [idToDelete, setIdToDelete] = useState(null);
  const [idToRestore, setIdToRestore] = useState(null);
  const [openDeleteHistory, setOpenDeleteHistory] = useState(false);
  const [openRestoreHistory, setOpenRestoreHistory] = useState(false);
  const [openTooltipId, setOpenTooltipId] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const tooltipWrapperRef = useClickOutsideTooltip(openTooltipId, setOpenTooltipId);
  //#endregion

  //state management
  useEffect(() => {
    dispatch(fetchMedicalHistory(patientId));
  }, []);

  const MedicalSurgeryTab = {
    NoMedicalSurgery: 0,
    MedicalSurgeryInformation: 1,
  };
  const [selectedTab, setSelectedTab] = useState(null);
  const [statusFilter, setstatusFilter] = useState("active");

  //#region helper functions
  const setTypeFilter = (filter) => {
    if (create) {
      setstatusFilter(filter);
    }
  };

  const hasActiveSurgeries = () =>
    medicalSurgeriesRecords.some((item) => item.status === "active");

  const renderCommentsTooltip = (comment) => (
    <div className="bg-white max-w-[460px]">
      <div className="flex flex-col gap-3 p-2">
        <div className="text-teal-300 text-sm-body prose-a:underline">
          <div className="flex flex-col gap-4">
            <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {formatWordsIntoLines(comment)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const isAnyHistory = () => {
    return medicalSurgeriesRecords.length > 0;
  };

  const manageClick = () => {
    document.body.classList.toggle("overflow-hidden");
  };
  //#endregion

  // Filtered surgeries for table and scrollbar logic
  const filteredSurgeries = medicalSurgeriesRecords.filter((val) =>
    statusFilter === "all" ? true : val.status === statusFilter
  );

  //#region Data update functions
  const deleteHistory = (type, recordId) => {
    dispatch(deleteMedicalSurgery(recordId));
  };

  const restoreHistory = (type, recordId) => {
    var records = medicalSurgeriesRecords;

    const restoreObject = records.find((item) => item.id === recordId);
    restoreObject.status = "active";
    restoreObject.confirmedDate = null;

    let obj = {
      noKnownMedicalHistory:
        records.every((val) => val.status === "inactive") &&
        medicalHistory.medicalStudies.every(
          (val) => val.status === "inactive"
        ) &&
        medicalHistory.surgeries.every((val) => val.status === "inactive"),
      medicalConditions:
        type === MedicalHistoryTypes.Condition
          ? records
          : medicalHistory.medicalConditions,
      medicalStudies: medicalHistory.medicalStudies,
      surgeries: medicalHistory.surgeries,
    };
    dispatch(updateMedicalHistory(patientId, obj, 'restored'));
  };
  //#endregion

  //#region UseEffect
  useEffect(() => {
    if (medicalHistory) {
      setMedicalSurgeriesRecords(medicalHistory.surgeries);

      if (
        medicalHistory.surgeries?.filter(
          (val) => val.status === "active"
        ).length > 0
      ) {
        setSelectedTab(MedicalSurgeryTab.MedicalSurgeryInformation);
      } else {
        setSelectedTab(MedicalSurgeryTab.NoMedicalSurgery);
      }
    }
  }, [medicalHistory]);
  //#endregion

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <div className="flex flex-1 h-[600px]">
            <div className="w-full gap-5 h-full">
              <div className="flex items-center justify-between mt-2.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                Medical History - Surgeries / Procedures
                </h4>
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>

              {/* option boxes for no medical histoy and medical history information */}
              <div
                id="medicalHistoryContainer"
                className="flex flex-col h-[590px] gap-6 overflow-scroll overflow-y-auto overflow-x-hidden bg-scroll"
              >
                {/* No medical condition */}
                <div
                  className={`flex justify-between p-6 border rounded 
                                        ${
                                          selectedTab ===
                                          MedicalSurgeryTab.NoMedicalSurgery
                                            ? "border-gray-100 border-2 bg-gray-20"
                                            : "border-gray-100"
                                        }`}
                >
                  <div className="flex">
                    <div className="flex-col justify-center items-center pr-6">
                      <h3>
                        <input
                          id="in-person"
                          name="visit-type"
                          onClick={() => {
                            if (!hasActiveSurgeries()) {
                              setSelectedTab(
                                MedicalSurgeryTab.NoMedicalSurgery
                              );
                            }
                          }}
                          value={
                            selectedTab ===
                            MedicalSurgeryTab.NoMedicalSurgery
                          }
                          checked={
                            selectedTab ===
                            MedicalSurgeryTab.NoMedicalSurgery
                          }
                          type="radio"
                          disabled={hasActiveSurgeries()}
                          className={`w-[45px] h-[45px] border-gray-100 form-radio text-REELTeal focus:ring-transparent ${
                            hasActiveSurgeries()
                              ? "cursor-not-allowed opacity-60"
                              : "cursor-pointer"
                          }`}
                          data-tooltip-id={
                            hasActiveSurgeries()
                              ? "no-surgeries-disabled-tooltip"
                              : undefined
                          }
                        />
                      </h3>
                    </div>
                    <div className="flex flex-col justify-center">
                      <h3
                        className={`text-xl font-semibold ${
                          hasActiveSurgeries() ? "text-gray-500" : "text-gray-900"
                        }`}
                      >
                        No Surgeries / Procedures
                      </h3>
                    </div>
                  </div>
                  {hasActiveSurgeries() && (
                    <NewToolTip
                      id="no-surgeries-disabled-tooltip"
                      place="bottom-start"
                      render={() => (
                        <div className="bg-gray-20 p-2 max-w-md flex flex-col">
                          <p className="text-teal-300 text-left text-body-sm font-normal">
                            Delete all Surgeries/ Procedures to enable 'No Surgeries / Procedures'.
                          </p>
                        </div>
                      )}
                    />
                  )}
                </div>
                {/* Medical condition information */}
                <div
                  className={`flex flex-col gap-6 p-6 border rounded ${
                    selectedTab ===
                    MedicalSurgeryTab.MedicalSurgeryInformation
                      ? "border-gray-100 border-2 bg-gray-20"
                      : "border-gray-100"
                  }`}
                >
                  {/* Start: Header */}
                  <div className="flex justify-between">
                    <div className="flex">
                      <div className="flex justify-center items-center pr-6">
                        <h3>
                          <input
                            id="in-person"
                            name="visit-type"
                            onClick={() => {
                              setSelectedTab(
                                MedicalSurgeryTab.MedicalSurgeryInformation
                              );

                              if (!isAnyHistory()) {
                                setShowCreateSlider(true);
                              } else {
                                setShowCreateSlider(false);
                              }
                            }}
                            value={
                              selectedTab ===
                              MedicalSurgeryTab.MedicalSurgeryInformation
                            }
                            checked={
                              selectedTab ===
                              MedicalSurgeryTab.MedicalSurgeryInformation
                            }
                            type="radio"
                            className="w-[45px] h-[45px] border-gray-100 cursor-pointer form-radio text-REELTeal focus:ring-transparent"
                          />
                        </h3>
                      </div>
                      <div className="flex flex-col">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {isAnyHistory()
                            ? "Surgeries/ Procedures Information"
                            : "Add Surgeries / Procedures"}
                        </h3>
                        <p className="text-base text-gray-800">
                          {isAnyHistory() && (
                            <span className="text-sm text-teal-300">
                              (Delete all Surgeries/ Procedures to enable ‘No Surgeries/ Procedures.)
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    {selectedTab ===
                      MedicalSurgeryTab.MedicalSurgeryInformation && (
                      <div className="flex flex-row pr-3.5">
                        {/* <Menu
                          as="div"
                          className="relative inline-block text-left pt-[6px]"
                        >
                          <div className="flex items-center">
                            <Menu.Button className="flex flex-row font-semibold mr-6 bg-white py-[14px] rounded-[4px] px-[16px] text-base min-w-[220px] h-full border-[0px] focus:outline-[0px] focus:ring-[0px] text-gray-900 p-0 focus:outline-none disabled:cursor-not-allowed text-left text-body-md items-center justify-between">
                              <span className="flex">
                                {
                                  StatusOptions.find(
                                    (x) => x.value === statusFilter
                                  ).label
                                }
                              </span>
                              <div className="flex flex-col">
                                <span>
                                  <ChevronDownIcon
                                    className={`w-5 transform transition-all `}
                                  />
                                </span>
                              </div>
                            </Menu.Button>
                          </div>
                          <Transition
                            as={Fragment}
                            enter="transition ease-out duration-100"
                            enterFrom="transform translate-y-2 opacity-0 scale-95"
                            enterTo="transform translate-y-0 opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform translate-y-0 opacity-100 scale-100"
                            leaveTo="transform translate-y-2 opacity-0 scale-95"
                          >
                            <Menu.Items className="min-w-[220px] absolute z-10 left-0 w-40 origin-top-left bg-white divide-y divide-gray-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                              {StatusOptions.map((status) => (
                                <Menu.Item
                                  className={`block w-full text-left px-3 py-3 border-b border-b-gray-20 
                                                                                ${
                                                                                  statusFilter ===
                                                                                    status.value &&
                                                                                  "bg-blue-100"
                                                                                } hover:bg-gray-50`}
                                  onClick={(e) => {
                                    setstatusFilter(status.value);
                                  }}
                                >
                                  <span>{status.label}</span>
                                </Menu.Item>
                              ))}
                            </Menu.Items>
                          </Transition>
                        </Menu> */}
                        <Button
                          className="flex rounded-[4px] text-base !w-[250px]"
                          type="primary"
                          text="Add Surgery / Procedure"
                          onClick={() => {
                            setShowCreateSlider(true);
                          }}
                        />
                      </div>
                    )}
                  </div>
                  {/* End: Header */}
                  {/* Start: Medical condition table */}
                  {selectedTab ===
                    MedicalSurgeryTab.MedicalSurgeryInformation &&
                    medicalSurgeriesRecords.length > 0 && (
                      <>
                        {filteredSurgeries.length > 0 && (
                          <div className="rounded-[6px] bg-white border border-gray-100 p-3">
                            <h4 className="text-2xl font-bold text-teal-600 pb-2 pl-2">
                              Surgeries/ Procedures
                            </h4>
                            <div className="rounded-[6px] border border-gray-100">
                              <div
                                className={`w-full ${
                                  filteredSurgeries.length > 6
                                    ? "overflow-y-auto scrollbar-hide max-h-[290px]"
                                    : ""
                                }`}
                              >
                              <table className="min-w-full divide-y bg-gray-50">
                                <thead className="bg-gray-20 items-start gap-4">
                                  <tr>
                                    <th
                                      scope="col"
                                      className="pl-6 w-[28%] text-base font-semibold text-left text-gray-900 py-4"
                                    >
                                      Surgeries/ Procedures
                                    </th>
                                    <th
                                      scope="col"
                                       className="w-[25%] text-base font-semibold text-left text-gray-900"
                                    >
                                      Status
                                    </th>
                                    <th
                                      scope="col"
                                       className="w-[12%] text-base font-semibold text-left text-gray-900"
                                    >
                                      Source
                                    </th>
                                    <th
                                      scope="col"
                                       className="w-[12%] text-base font-semibold text-left text-gray-900"
                                    >
                                      Diagnosis Date
                                    </th>
                                    <th
                                      scope="col"
                                    className="w-[14%] text-base font-semibold text-center text-gray-900"
                                    >
                                      Comments
                                    </th>
                                    <th
                                      scope="col"
                                       className="w-[16%] text-base font-semibold text-center text-gray-900"
                                    > Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="min-w-full divide-y bg-white gap-4">
                                  {filteredSurgeries.map((sur, i) => {
                                      const isStripedRow = i % 2 === 1;
                                      return (
                                      <tr
                                        className={`${isStripedRow ? "bg-gray-10" : "bg-white"} transition-colors duration-300 m-4 cursor-pointer group `}
                                        key={sur.id}
                                      >
                                        <td
                                          className={`pl-6 py-2 w-[28%] text-base font-medium text-teal-300 whitespace-nowrap 
                                          ${
                                            sur.surgeryGenCode === null
                                              ? "text-pink"
                                              : "text-gray-800"
                                          }`}
                                          data-tooltip-id={
                                            sur.surgeryGenCode === null
                                              ? "ma-uncoded-medSurgery"
                                              : ""
                                          }
                                        >
                                          <span className="capitalize">
                                            {sur.name}
                                          </span>
                                          {sur.surgeryGenCode === null && (
                                            <NewToolTip
                                              id="ma-uncoded-medSurgery"
                                              render={() => <UncodedMedical />}
                                            />
                                          )}
                                        </td>
                                         <td className="w-[25%] text-base font-medium text-gray-800 whitespace-nowrap">
                                          {sur.status}
                                        </td>
                                         <td className="w-[12%] text-base font-medium text-gray-800 whitespace-nowrap">
                                          {sur.source}
                                        </td>
                                         <td className="w-[12%] text-base text-gray-800 whitespace-nowrap">
                                          {formatDiagnosisDateForDisplay(sur.date, sur.surgeryDateOption)}
                                        </td>
                                        <td className="w-[14%] text-center text-base font-medium text-gray-800 whitespace-nowrap">
                                          {sur.comments && sur.comments.length > 0 && (
                                            <div className="inline-flex justify-center items-center pt-2 px-4">
                                              <div
                                                ref={openTooltipId === `patientMedSurgComments-${sur.id}` ? tooltipWrapperRef : null}
                                                className="relative inline-flex cursor-pointer"
                                                data-tooltip-id={`patientMedSurgCommentsHover-${sur.id}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const tooltipId = `patientMedSurgComments-${sur.id}`;
                                                  setOpenTooltipId(
                                                    openTooltipId === tooltipId
                                                      ? null
                                                      : tooltipId
                                                  );
                                                }}
                                              >
                                                <ReactSVG
                                                  src="/icons/paper-clip-new.svg"
                                                  className="w-6"
                                                />
                                                {openTooltipId !==
                                                  `patientMedSurgComments-${sur.id}` && (
                                                  <NewToolTip
                                                    id={`patientMedSurgCommentsHover-${sur.id}`}
                                                    render={() => (
                                                      <div className="bg-white p-2 text-teal-300 text-body-sm">
                                                        Click to view comments
                                                      </div>
                                                    )}
                                                  />
                                                )}
                                                <NewToolTip
                                                  id={`patientMedSurgComments-${sur.id}`}
                                                  isOpen={
                                                    openTooltipId ===
                                                    `patientMedSurgComments-${sur.id}`
                                                  }
                                                  render={() =>
                                                    renderCommentsTooltip(
                                                      sur.comments
                                                    )
                                                  }
                                                  clickable={true}
                                                  handleHide={() =>
                                                    setOpenTooltipId(null)
                                                  }
                                                  anchorSelect={`[data-tooltip-id="patientMedSurgCommentsHover-${sur.id}"]`}
                                                />
                                              </div>
                                            </div>
                                          )}
                                        </td>
                                        <td className="w-[16%] text-base font-medium text-gray-800 whitespace-nowrap">
                                          {sur.status !== "inactive" && (
                                            <div className="flex items-center pl-4">
                                              <div className="flex flex-1 justify-center">
                                                <Menu
                                                  as="div"
                                                  className="relative inline-block text-left"
                                                >
                                                  {({ open }) => {
                                                    // Track which row's menu is open
                                                    if (
                                                      open &&
                                                      openMenuIndex !== i
                                                    ) {
                                                      setOpenMenuIndex(i);
                                                    } else if (
                                                      !open &&
                                                      openMenuIndex === i
                                                    ) {
                                                      setOpenMenuIndex(null);
                                                    }

                                                    // Hide icon if it's directly below an open menu
                                                    const shouldHideIcon =
                                                      openMenuIndex === i - 1;

                                                    return (
                                                      <>
                                                        <div>
                                                          <Menu.Button>
                                                            <ReactSVG
                                                              src={`/icons/triple-dot.svg`}
                                                              className={`flex text-black !items-center w-6 ${
                                                                shouldHideIcon
                                                                  ? "opacity-0 pointer-events-none"
                                                                  : ""
                                                              }`}
                                                            />
                                                          </Menu.Button>
                                                        </div>
                                                        <Transition
                                                          as={Fragment}
                                                          enter="transition ease-out duration-100"
                                                          enterFrom="transform translate-y-2 opacity-0 scale-95"
                                                          enterTo="transform translate-y-0 opacity-100 scale-100"
                                                          leave="transition ease-in duration-75"
                                                          leaveFrom="transform translate-y-0 opacity-100 scale-100"
                                                          leaveTo="transform translate-y-2 opacity-0 scale-95"
                                                        >
                                                          <Menu.Items
                                                            className={
                                                              i === filteredSurgeries.length - 1 || filteredSurgeries.length === 1
                                                                ? "absolute right-full mr-[6px] min-w-[70px] w-auto origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 top-0"
                                                                : "absolute right-4 top-6 w-18 origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
                                                            }
                                                          >
                                                            <Menu.Item>
                                                              <button
                                                                className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center border-b border-b-gray-20 hover:bg-gray-100"
                                                                onClick={() => {
                                                                  setSelectedHistory(
                                                                    sur
                                                                  );
                                                                  setSelectedHistoryType(
                                                                    MedicalHistoryTypes.Surgery
                                                                  );
                                                                  setCreate(
                                                                    false
                                                                  );
                                                                  setShowCreateSlider(
                                                                    true
                                                                  );
                                                                }}
                                                              >
                                                                Edit
                                                              </button>
                                                            </Menu.Item>
                                                            <Menu.Item>
                                                              <button
                                                                className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center border-b border-b-gray-20 hover:bg-gray-100"
                                                                onClick={() => {
                                                                  setSelectedHistory(
                                                                    sur
                                                                  );
                                                                  setSelectedHistoryType(
                                                                    MedicalHistoryTypes.Surgery
                                                                  );
                                                                  setOpenDeleteHistory(
                                                                    true
                                                                  );
                                                                  setIdToDelete(
                                                                    sur.id
                                                                  );
                                                                }}
                                                              >
                                                                Delete
                                                              </button>
                                                            </Menu.Item>
                                                          </Menu.Items>
                                                        </Transition>
                                                      </>
                                                    );
                                                  }}
                                                </Menu>
                                              </div>
                                            </div>
                                          )}
                                          {sur.status === "inactive" && (
                                            <div className="flex justify-end pl-16">
                                              <ReactSVG
                                                src="/icons/undo.svg"
                                                className="w-6"
                                                onClick={() => {
                                                  setSelectedHistory(sur);
                                                  selectedHistoryType(
                                                    MedicalHistoryTypes.Surgery
                                                  );
                                                  setOpenRestoreHistory(true);
                                                  setIdToRestore(sur.id);
                                                }}
                                              />
                                            </div>
                                          )}
                                        </td>
                                      </tr>
                                  );
                                  })}
                                </tbody>
                              </table>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  {/* End: Medical condition table */}

                  {/* Start: Edit/Delete/Restore pop-ups */}
                  <ConfirmationModalNew
                    type="primary"
                    title={`Delete ${getHistoryTypeName(selectedHistoryType)}`}
                    content={() =>
                      `Are you sure you want to delete ${capitalizeFirstLetter(
                        selectedHistory?.name ?? ""
                      )}?`
                    }
                    confirmAction={() => {
                      deleteHistory(selectedHistoryType, idToDelete);
                      setSelectedHistory(null);
                      setOpenDeleteHistory(false);
                      setIdToDelete(null);
                    }}
                    optionLabel="Reason"
                    open={openDeleteHistory}
                    handleClose={() => {
                      setSelectedHistory(null);
                      setOpenDeleteHistory(false);
                      setIdToDelete(null);
                    }}
                    onCross={() => {
                      setSelectedHistory(null);
                      setOpenDeleteHistory(false);
                      setIdToDelete(null);
                    }}
                    options={DeleteOptions}
                  />
                  <ConfirmationModalNew
                    open={openRestoreHistory}
                    title={`Restore ${getHistoryTypeName(selectedHistoryType)}`}
                    type="primary"
                    confirmText="Yes"
                    content={() => (
                      <p className="p-7">
                        Are you sure you wish to restore this{" "}
                        {getHistoryTypeName(selectedHistoryType)}?
                      </p>
                    )}
                    onCross={() => {
                      setSelectedHistory(null);
                      setOpenRestoreHistory(false);
                      setIdToRestore(null);
                    }}
                    handleClose={() => {
                      setSelectedHistory(null);
                      setOpenRestoreHistory(false);
                      setIdToRestore(null);
                    }}
                    confirmAction={() => {
                      restoreHistory(selectedHistoryType, idToRestore);
                      setSelectedHistory(null);
                      setOpenRestoreHistory(false);
                      setIdToRestore(null);
                    }}
                  />
                  {/* End: Edit/Delete/Restore pop-ups */}

                  <MedicalHistoryCreate
                    open={showCreateSlider}
                    onClose={() => {
                      setShowCreateSlider(false);
                      manageClick();
                      setSelectedHistory(null);
                      setCreate(true);
                    }}
                    selectedHistoryType={selectedHistoryType}
                    onSave={() => {
                      setTypeFilter("all");
                    }}
                    selectedHistory={selectedHistory}
                    patientId={patientId}
                    create={create}
                    // patientMedicalConditions={medicalSurgeriesRecords}
                    // // patientMedicalStudies={medicalStudyRecords}
                    patientMedicalSurgeries={medicalSurgeriesRecords}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientSurgeries;
